import React from 'react'
import { Redirect } from 'react-router-dom'
import {
  Segment,
  Table,
  Grid,
  Header,
  Icon,
  Button,
  Message,
  Dropdown,
  Loader,
  Label,
  Modal,
} from 'semantic-ui-react'

import { statusLabel, isApproved } from '../../utils/workflowStatus'
import {
  competenciesForTerm,
  skillsForTerm,
  ratingKey,
  countSlots,
  countFilled,
  skillsUsedBy,
  MACRO_SKILL_LABELS,
} from './paceGrid'
import './Pace.css'

const SKILL_CLASSES = {
  L: 'pace-skill-listening',
  S: 'pace-skill-speaking',
  R: 'pace-skill-reading',
  W: 'pace-skill-writing',
}

/**
 * The PACE entry grid.
 *
 * A dense grid of small native selects rather than Semantic dropdowns: Reading
 * and Literacy renders around forty rows with up to four ratings each, and a
 * hundred and sixty Semantic dropdowns make the page crawl. A select is also
 * what a teacher can tab through fastest.
 */
const PacePV = (props) => {
  if (!props.auth) return <Redirect to="/" />

  const {
    loading, error, success, saving, dirty,
    section, sectionSubject, scheme, term,
    sectionStudents, sectionStudentId, competencies, ratings,
    sectionSubjectDetail, canEdit, pendingStudentId,
  } = props

  if (loading) return <Loader active inline="centered" />

  if (error && !sectionSubject) {
    return <Segment textAlign="center">{error}</Segment>
  }

  if (!scheme) return <Loader active inline="centered" />

  // usesPace, not mode: Grade 1's subject marks are numbers now, but the
  // competency form is a separate assessment and still rated A-E.
  if (!scheme.usesPace) {
    return (
      <Segment textAlign="center">
        {section.gradeLevel} is graded numerically this school year, so it uses the
        grades page rather than the PACE form.
      </Segment>
    )
  }

  const studentOptions = sectionStudents.map((s) => ({
    key: s.sectionStudentId,
    value: s.sectionStudentId,
    text: `${s.student.lastName}, ${s.student.firstName}`,
  }))

  const ratingOf = (competency, macroSkill) =>
    ratings[ratingKey(competency.competencyId, macroSkill)] || ''

  const ratingSelect = (competency, macroSkill) => {
    const label = macroSkill ? MACRO_SKILL_LABELS[macroSkill] : 'Rating'
    return (
      <select
        key={macroSkill || 'single'}
        className={`pace-rating ${macroSkill ? SKILL_CLASSES[macroSkill] : ''}`}
        title={label}
        aria-label={`${label} for ${competency.text}`}
        value={ratingOf(competency, macroSkill)}
        disabled={!canEdit}
        onChange={(e) =>
          props.onRatingChange(competency.competencyId, macroSkill, e.target.value)
        }
      >
        <option value="">-</option>
        {scheme.scaleOptions.map((option) => (
          <option key={option.value} value={option.value}>{option.value}</option>
        ))}
      </select>
    )
  }

  const visible = competenciesForTerm(competencies, term)
  const skillsUsed = skillsUsedBy(competencies)
  const totalSlots = countSlots(competencies, term)
  const filledSlots = countFilled(competencies, term, ratings)

  let currentDomain = null
  const rows = []

  visible.forEach((competency) => {
    if (competency.domain && competency.domain !== currentDomain) {
      currentDomain = competency.domain
      rows.push(
        <Table.Row key={`domain-${competency.competencyId}`} className="pace-domain-row">
          <Table.Cell colSpan="3"><strong>{competency.domain}</strong></Table.Cell>
        </Table.Row>
      )
    }

    const skills = skillsForTerm(competency, term)

    rows.push(
      <Table.Row key={competency.competencyId}>
        <Table.Cell collapsing textAlign="center" className="pace-label">
          {competency.label}{competency.subLabel}
        </Table.Cell>
        <Table.Cell className={competency.subLabel ? 'pace-sub' : undefined}>
          {competency.text}
          {competency.performanceStandard && (
            <div className="pace-standard">{competency.performanceStandard}</div>
          )}
          {competency.needsVerification && (
            <Label size="mini" color="yellow" horizontal className="pace-flag">
              check against printed form
            </Label>
          )}
        </Table.Cell>
        <Table.Cell collapsing textAlign="center">
          {/* A header row is a group stem, greyed on the form and never rated;
              an empty skill list means this term is not assessed at all. */}
          {competency.isHeader
            ? null
            : skills.length === 0
              ? <span className="pace-not-assessed">not assessed this term</span>
              : skills.map((skill) => ratingSelect(competency, skill))}
        </Table.Cell>
      </Table.Row>
    )
  })

  const flaggedCount = visible.filter((c) => c.needsVerification).length

  return (
    <div>
      <Grid>
        <Grid.Row>
          <Grid.Column width={10}>
            <Header as="h2">
              {sectionSubject.subject.name}
              <Header.Subheader>
                {section.gradeLevel} - {section.name}
                {'  ·  '}
                {scheme.periods.filter((p) => p.index === term).map((p) => p.label)}
              </Header.Subheader>
            </Header>
            {/* Entry happens here, but submission and approval live on the
                grades page - these ratings ride on that submission. Without
                the status showing, a frozen grid just looks broken. */}
            {sectionSubjectDetail && (
              <Label
                horizontal
                color={isApproved(sectionSubjectDetail.status) ? 'green' : 'yellow'}
                className="pace-status"
              >
                {statusLabel(sectionSubjectDetail.status)}
              </Label>
            )}
          </Grid.Column>
          <Grid.Column width={6} textAlign="right">
            <span className="pace-progress">
              {filledSlots} of {totalSlots} rated
            </span>
            {sectionStudentId && (
              <Button
                basic
                as="a"
                href={`/pace/print/${sectionStudentId}`}
                title="Open the printable PACE form for this learner"
              >
                <Icon name="print" /> Form
              </Button>
            )}
            <Button primary loading={saving} disabled={saving || !dirty || !canEdit} onClick={props.onSave}>
              <Icon name="save" /> Save ratings
            </Button>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={8}>
            <Dropdown
              fluid
              search
              selection
              placeholder="Select a learner"
              options={studentOptions}
              value={sectionStudentId}
              onChange={props.onStudentChange}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <div className="pace-legend">
              {scheme.scaleOptions.map((option) => (
                <span key={option.value} className="pace-legend-item">
                  <strong>{option.value}</strong> {option.text.replace(option.value + ' - ', '')}
                </span>
              ))}
            </div>
          </Grid.Column>
        </Grid.Row>

        {error && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Message error header="Could not save" content={error} />
            </Grid.Column>
          </Grid.Row>
        )}

        {success && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Message success content="Ratings saved." />
            </Grid.Column>
          </Grid.Row>
        )}

        {/* No workflow row means the grid is frozen with nothing to explain it,
            which is exactly the silent-freeze this message exists to prevent. */}
        {!sectionSubjectDetail && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Message warning>
                <Message.Header>Could not read the submission status</Message.Header>
                <p>
                  Entry is disabled because there is no workflow row for this subject
                  and term, so there is no way to tell whether the marks are still
                  with the teacher. Refresh the page; if it persists, the section may
                  be missing its workflow rows.
                </p>
              </Message>
            </Grid.Column>
          </Grid.Row>
        )}

        {/* Read-only because the submission has moved up the ladder. Say so,
            and point at the page where it can be sent back. */}
        {sectionSubjectDetail && !canEdit && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Message info>
                <Message.Header>
                  These ratings are {statusLabel(sectionSubjectDetail.status).toLowerCase()} and can no longer be edited here
                </Message.Header>
                <p>
                  PACE ratings are part of the grade submission for this subject and
                  term, so they freeze when it does.{' '}
                  <a href={`/grades/${section.sectionId}/${sectionSubject.sectionSubjectId}?quarter=${term}`}>
                    Open the grades page
                  </a>{' '}
                  to see the approval trail or send it back for resubmission.
                </p>
              </Message>
            </Grid.Column>
          </Grid.Row>
        )}

        {flaggedCount > 0 && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Message warning>
                <Message.Header>{flaggedCount} rows need checking against the printed form</Message.Header>
                <p>
                  Which macro skills apply to a competency in a given term is shown on
                  the PACE form by cell colour, and colour does not survive the import.
                  Until these are confirmed, every skill is offered rather than the
                  correct subset.
                </p>
              </Message>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>

      <Segment>
        {competencies.length === 0 ? (
          <div className="pace-empty">
            No competencies have been loaded for this subject yet. Run the PACE seed
            script on the server, then reload this page.
          </div>
        ) : (
          <React.Fragment>
            {/* Only the skills this subject actually uses. Language has two,
                Reading and Literacy has four - matching the printed form. */}
            {skillsUsed.length > 0 && (
              <div className="pace-skill-legend">
                {skillsUsed.map((key) => (
                  <span key={key} className="pace-legend-item">
                    <span className={`pace-swatch ${SKILL_CLASSES[key]}`} />
                    {MACRO_SKILL_LABELS[key]}
                  </span>
                ))}
              </div>
            )}
            <Table celled compact size="small" className="pace-table">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell collapsing textAlign="center">No.</Table.HeaderCell>
                  <Table.HeaderCell>Learning Competency</Table.HeaderCell>
                  <Table.HeaderCell collapsing textAlign="center">Rating</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>{rows}</Table.Body>
            </Table>
          </React.Fragment>
        )}
      </Segment>

      {/* Ratings live in local state until Save, so a switch would drop them. */}
      <Modal open={!!pendingStudentId} onClose={props.onCancelSwitch} size="small" closeIcon>
        <Modal.Header>You have unsaved ratings</Modal.Header>
        <Modal.Content>
          <p>
            The ratings entered for this learner have not been saved yet.
            Switching to another learner will discard them.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={props.onCancelSwitch}>Stay and save</Button>
          <Button color="orange" onClick={props.onDiscardAndSwitch}>
            <Icon name="trash" /> Discard and switch
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  )
}

export default PacePV
